import React, {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState
} from 'react';
import { NavigateOptions, useNavigate } from 'react-router-dom';

import { useDynamicFormDrawerStyles } from '@components/dynamic-form/style';
import CloseIcon from '@mui/icons-material/Close';
import { Drawer, IconButton } from '@mui/material';
import { MAIN_THEME } from '@styles';

import { AccountingTransferDrawer } from './AccountingTransfer.drawer';
import { ActivateLinesAndPlansDrawer } from './activateLinesAndPlans.drawer';
import { AddDocumentDrawer } from './addDocument.drawer';
import { CloseClaimDrawer } from './closeClaim.drawer';
import { CloseProposalDrawer } from './closeProposal.drawer';
import { DeactivateProviderDrawer } from './deactivateProvider.drawer';
import { ExportAmendmentsDrawer } from './exportAmendments.drawer';
import { ExportBusinessPartnersDrawer } from './exportBusinessPartners.drawer';
import { ExportClaimsDrawer } from './exportClaims.drawer';
import { ExportCurrencyRateDrawer } from './exportCurrencyRate.drawer';
import { ExportPoliciesDrawer } from './exportPolicies.drawer';
import { ExportPolicyDistributionDrawer } from './exportPolicyDistribution.drawer';
import { ExportProposalsDrawer } from './exportProposals.drawer';
import { ExportReinsurersDrawer } from './exportReinsurers.drawer';
import { ExportTaxationDrawer } from './exportTaxation.drawer';
import { ExportTreatiesDrawer } from './exportTreaties.drawer';
import { ModifyAddressDrawer } from './ModifyAddressDrawer';
import { ModifyAmendmentDrawer } from './modifyAmendment.drawer';
import { ModifyBusinessPartnerDrawer } from './modifyBusinessPartner.drawer';
import { ModifyCurrencyRateDrawer } from './modifyCurrencyRate.drawer';
import { ModifyPlanSpecificTaxationDrawer } from './modifyPlanSpecificTaxation.drawer';
import { ModifyProviderDrawer } from './modifyProvider.drawer';
import { ModifyTaxationDrawer } from './modifyTaxation.drawer';
import { NewAmendmentDrawer } from './newAmendment.drawer';
import { NewBusinessPartnerDrawer } from './newBusinessPartner.drawer';
import { NewClaimDrawer } from './newClaim.drawer';
import { NewCurrencyRateDrawer } from './newCurrencyRate.drawer';
import { NewPlanSpecificTaxationDrawer } from './newPlanSpecificTaxation.drawer';
import { NewProposalDrawer } from './newProposalDrawer';
import { NewTaxationDrawer } from './newTaxation.drawer';
import { NewTreatyDrawer } from './newTreaty.drawer';
import { ProviderDrawer } from './provider.drawer';
import { ReactivateTreatyDrawer } from './reactivateTreaty.drawer';
import { SetLineDefaultCommissionDrawer } from './setLineDefaultComission.drawer';
import { SetPLansCommissionDrawer } from './setPlansCommission.drawer';
import { UpdateReinsurersDrawer } from './updateReinsurers.drawer';

type DrawerState = {
  isOpen: boolean;
  name: string;
  title: string;
  props?: any;
};

type DrawerContextType = {
  drawer: DrawerState;
  openDrawer: (name: string, title: string, props?: any) => void;
  closeDrawer: () => void;
};

const DrawerContext = createContext<DrawerContextType | undefined>(undefined);

export function useDrawer() {
  const context = useContext(DrawerContext);
  if (!context) {
    throw new Error('useDrawer must be used within DrawerProvider');
  }

  return context;
}

const DrawerProvider: React.FC<{ children: React.ReactNode }> = ({
  children
}) => {
  const { classes } = useDynamicFormDrawerStyles();
  const navigate = useNavigate();

  const [drawer, setDrawer] = useState<DrawerState>({
    isOpen: false,
    name: '',
    title: ''
  });

  const openDrawer = useCallback((name: string, title: string, props?: any) => {
    setDrawer({ isOpen: true, name, title, props });
  }, []);

  const closeDrawer = useCallback(
    (urlDetailsPath?: string, options?: NavigateOptions) => {
      setDrawer((prev) => ({ ...prev, isOpen: false }));
      if (urlDetailsPath) {
        navigate(urlDetailsPath, options);
      }
    },
    []
  );

  const DrawerHeader = () => {
    return (
      <header className={classes.drawerHeader}>
        <figure className={classes.sectionFigure} />
        <span className={classes.drawerHeaderTitle}>{drawer.title}</span>
        <IconButton
          aria-label="close"
          onClick={() => closeDrawer('')}
          sx={{ margin: '0 15px 0 0' }}
        >
          <CloseIcon sx={{ color: MAIN_THEME.color.white }} />
        </IconButton>
      </header>
    );
  };

  const DrawerBody = () => {
    switch (drawer.name) {
      case 'provider':
        return (
          <ProviderDrawer
            onSuccess={(id: string) =>
              closeDrawer(`salesforce/providers/${id}`)
            }
          />
        );
      case 'updateProvider':
        return (
          <ModifyProviderDrawer
            onSuccess={() => closeDrawer('')}
            data={drawer.props}
          />
        );
      case 'deactivateProvider':
        return (
          <DeactivateProviderDrawer
            onSuccess={closeDrawer}
            data={drawer.props}
          />
        );
      case 'accountingTransfer':
        return (
          <AccountingTransferDrawer
            onSuccess={() => closeDrawer('')}
            companyId={drawer.props?.companyId}
            refetch={drawer.props?.refetch}
          />
        );
      case 'newAmendment':
        return (
          <NewAmendmentDrawer
            policyId={drawer?.props?.policyId}
            policyLineCode={drawer?.props?.policyLineCode}
            policyData={drawer?.props?.policyData}
            onSuccess={(urlPath) => closeDrawer(urlPath)}
          />
        );
      case 'newProposal':
        return (
          <NewProposalDrawer
            renewalContext={drawer.props?.renewalContext}
            onSuccess={(urlPath) => closeDrawer(urlPath)}
          />
        );
      case 'exportProposals':
        return <ExportProposalsDrawer onSuccess={() => closeDrawer()} />;
      case 'exportPolicies':
        return <ExportPoliciesDrawer onSuccess={() => closeDrawer()} />;
      case 'exportAmendments':
        return <ExportAmendmentsDrawer onSuccess={() => closeDrawer()} />;
      case 'modifyAmendment':
        return (
          <ModifyAmendmentDrawer
            onSuccess={() => closeDrawer()}
            amendmentId={drawer.props?.amendmentId}
            amendmentType={drawer.props?.amendmentType}
            systemReferenceDate={drawer.props?.systemReferenceDate}
            policyEffectiveDate={drawer.props?.policyEffectiveDate}
            policyExpiryDate={drawer.props?.policyExpiryDate}
            initialValues={drawer.props?.initialValues}
          />
        );
      case 'closeProposal':
        return (
          <CloseProposalDrawer
            onSuccess={() => closeDrawer()}
            id={drawer.props?.proposalId}
          />
        );
      case 'addDocument':
        return (
          <AddDocumentDrawer
            onSuccess={() => closeDrawer()}
            addDocumentMutation={drawer.props?.addDocumentMutation}
            addDocumentVariables={drawer.props?.addDocumentVariables}
            refetchQueries={drawer.props?.refetchQueries}
          />
        );
      case 'newClaim':
        return <NewClaimDrawer onSuccess={(urlPath) => closeDrawer(urlPath)} />;
      case 'closeClaim':
        return (
          <CloseClaimDrawer
            onSuccess={() => closeDrawer()}
            claimId={drawer.props?.claimId}
            claimStatus={drawer.props?.claimStatus}
          />
        );
      case 'exportClaims':
        return <ExportClaimsDrawer onSuccess={() => closeDrawer()} />;
      case 'exportReinsurers':
        return <ExportReinsurersDrawer onSuccess={() => closeDrawer()} />;
      case 'exportTreaties':
        return <ExportTreatiesDrawer onSuccess={() => closeDrawer()} />;
      case 'newTreaty':
        return (
          <NewTreatyDrawer onSuccess={(urlPath) => closeDrawer(urlPath)} />
        );
      case 'updateReinsurers':
        return (
          <UpdateReinsurersDrawer
            onSuccess={() => closeDrawer()}
            relatedCompany={drawer.props?.relatedCompany}
            treatyId={drawer.props?.treatyId}
          />
        );
      case 'exportPolicyDistribution':
        return (
          <ExportPolicyDistributionDrawer onSuccess={() => closeDrawer()} />
        );
      case 'reactivateTreaty':
        return (
          <ReactivateTreatyDrawer
            onSuccess={() => closeDrawer()}
            treatyId={drawer.props?.treatyId}
          />
        );
      case 'newCurrencyRate':
        return (
          <NewCurrencyRateDrawer
            onSuccess={(urlPath, options) => closeDrawer(urlPath, options)}
          />
        );

      case 'modifyCurrencyRate':
        return (
          <ModifyCurrencyRateDrawer
            onSuccess={(urlPath, options) => closeDrawer(urlPath, options)}
          />
        );
      case 'exportCurrencyRate':
        return <ExportCurrencyRateDrawer onSuccess={() => closeDrawer()} />;
      case 'newTaxation':
        return (
          <NewTaxationDrawer
            onSuccess={(urlPath) => closeDrawer(urlPath)}
            activeLines={drawer.props?.activeLines}
          />
        );
      case 'exportTaxation':
        return <ExportTaxationDrawer onSuccess={() => closeDrawer()} />;
      case 'newBusinessPartner':
        return (
          <NewBusinessPartnerDrawer
            onSuccess={(urlPath) => closeDrawer(urlPath)}
          />
        );
      case 'exportBusinessPartners':
        return <ExportBusinessPartnersDrawer onSuccess={() => closeDrawer()} />;
      case 'modifyBusinessPartner':
        return (
          <ModifyBusinessPartnerDrawer
            onSuccess={() => closeDrawer()}
            businessPartnerId={drawer.props?.businessPartnerId}
            businessPartnerDetails={drawer.props?.businessPartnerDetails}
          />
        );
      case 'modifyAddress':
        return (
          <ModifyAddressDrawer
            onSuccess={() => closeDrawer('')}
            addressId={drawer.props?.addressId}
            addressDetails={drawer.props?.addressDetails}
          />
        );
      case 'modifyTaxation':
        return (
          <ModifyTaxationDrawer
            onSuccess={() => closeDrawer()}
            taxationId={drawer.props?.taxationId}
          />
        );
      case 'newPlanSpecificTaxation':
        return (
          <NewPlanSpecificTaxationDrawer
            onSuccess={() => {
              closeDrawer('');
              drawer.props?.onSuccess?.();
            }}
            taxationId={drawer.props?.taxationId}
            taxationLine={drawer.props?.taxationLine}
          />
        );
      case 'modifyPlanSpecificTaxation':
        return (
          <ModifyPlanSpecificTaxationDrawer
            onSuccess={() => {
              closeDrawer('');
              drawer.props?.onSuccess?.();
            }}
            taxationPlanSpecificId={drawer.props?.taxationPlanSpecificId}
          />
        );
      case 'activateLinesAndPlans':
        return (
          <ActivateLinesAndPlansDrawer
            onSuccess={() => {
              drawer.props?.onSuccess?.();
              closeDrawer('');
            }}
            businessPartnerId={drawer.props?.businessPartnerId}
            isDeactivate={drawer.props?.isDeactivate}
          />
        );
      case 'setLineDefaultCommission':
        return (
          <SetLineDefaultCommissionDrawer
            onSuccess={() => {
              drawer.props?.onSuccess?.();
              closeDrawer('');
            }}
            businessPartnerId={drawer.props?.businessPartnerId}
            assignedLineId={drawer.props?.assignedLineId}
          />
        );
      case 'setPlansCommission':
        return (
          <SetPLansCommissionDrawer
            onSuccess={() => {
              drawer.props?.onSuccess?.();
              closeDrawer('');
            }}
            businessPartnerId={drawer.props?.businessPartnerId}
            selectedPlans={drawer.props?.selectedPlans}
            selectedPlansCodes={drawer.props?.selectedPlansCodes}
            defaultCommissionType={drawer.props?.defaultCommissionType}
            defaultCommissionRate={drawer.props?.defaultCommissionRate}
            defaultCommissionAmount={drawer.props?.defaultCommissionAmount}
            defaultCommissionCurrency={drawer.props?.defaultCommissionCurrency}
          />
        );
      // case 'assignPlans':
      //   return (
      //     <AssignPlansDrawer
      //       onSuccess={() => {
      //         drawer.props?.onSuccess?.();
      //         closeDrawer('');
      //       }}
      //       businessPartnerId={drawer.props?.businessPartnerId}
      //       isDirectSalesforce={drawer.props?.isDirectSalesforce}
      //       defaultCommissionType={drawer.props?.defaultCommissionType}
      //     />
      //   );
      default:
        return null;
    }
  };

  const contextValue = useMemo(
    () => ({ drawer, openDrawer, closeDrawer }),
    [drawer, openDrawer, closeDrawer]
  );

  return (
    <DrawerContext.Provider value={contextValue}>
      {children}
      {/* Always rendered globally */}
      <Drawer
        open={drawer.isOpen}
        anchor="bottom"
        onClose={() => closeDrawer('')}
        transitionDuration={400}
        classes={{ paperAnchorBottom: classes.drawer }}
      >
        {drawer.isOpen && (
          <>
            <DrawerHeader />
            <DrawerBody />
          </>
        )}
      </Drawer>
    </DrawerContext.Provider>
  );
};

export { DrawerProvider };
